import Vue from 'vue'
import { MixedVueClass, VueClass } from './types'
import { container } from 'tsyringe'
import { PanelType } from '@/modules/shared/interfaces/panel-type'

class FacadeClass {
  static resolvedInstance: any = null

  static getFacadeAccessor() {
    throw new Error('Facade does not implement getFacadeAccessor method.')
  }

  protected static resolveFacadeInstance(name) {
    if (typeof name === 'object') {
      return this.resolvedInstance = name
    }

    return this.resolvedInstance = container.resolve(name)
  }

  static getFacadeRoot() {
    if (this.resolvedInstance) {
      return this.resolvedInstance
    }
    return this.resolveFacadeInstance(this.getFacadeAccessor())
  }

  static __callStatic(method, args) {
    const instance = this.getFacadeRoot()

    if (!instance) {
      throw new Error('A facade root has not been set.')
    }

    return instance[method](...args)
  }
}

const facade = new Proxy(FacadeClass, {
  get(target, p: PropertyKey, receiver: any): any {
    if (Reflect.has(target, p) || typeof p !== 'string') {
      return Reflect.get(target, p, receiver)
    } else {
      return (...args) => {
        return target.__callStatic.call(receiver, p, args)
      }
    }
  }
})

type FacadeConstructor<T> = {
  new(...args: any[]): FacadeClass & T
} & T

export function Facade<T>(): FacadeConstructor<T> {
  return facade as any
}

export async function moduleResolver(name: string, type: PanelType) {
  let module: any

  function pathResolver(path: string) {
    const slashIndex: number = path.indexOf('/')

    if(slashIndex < 0){
      return {
        moduleName: path,
        componentName: 'index'
      }
    }

    return {
      moduleName: path.slice(0, slashIndex),
      componentName: path.slice(slashIndex + 1)
    }
  }

  const { moduleName, componentName } = pathResolver(name)
  const panelDir = type

  try {
    module = await import(
      /* webpackInclude: /\.page\.vue$/ */
      `@/modules/${moduleName}/pages/${panelDir}/${componentName}.page.vue`
    )
  } catch (error) {
    console.log('error')
    // try {
    //   module = await import(
    //     /* webpackInclude: /\.page\.vue$/ */
    //     `@/ui/${panelDir}/${name}.page.vue`
    //     )
    // } catch (error) {
    //   module = await import(
    //     /* webpackInclude: /\.page\.vue$/ */
    //     `@/ui/${panelDir}/${name}/index.page.vue`
    //     )
    // }
  }

  if (!module.default) {
    throw new Error(`Module ${name} has no default export.`)
  }
  return module.default
}

export function mixins<A>(CtorA: VueClass<A>): VueClass<A>;
export function mixins<A, B>(CtorA: VueClass<A>, CtorB: VueClass<B>): VueClass<A & B>;
export function mixins<A, B, C>(CtorA: VueClass<A>, CtorB: VueClass<B>, CtorC: VueClass<C>): VueClass<A & B & C>;
export function mixins<A, B, C, D>(CtorA: VueClass<A>, CtorB: VueClass<B>, CtorC: VueClass<C>, CtorD: VueClass<D>): VueClass<A & B & C & D>;
export function mixins<A, B, C, D, E>(CtorA: VueClass<A>, CtorB: VueClass<B>, CtorC: VueClass<C>, CtorD: VueClass<D>, CtorE: VueClass<E>): VueClass<A & B & C & D & E>;
export function mixins<T>(...Ctors: VueClass<Vue>[]): VueClass<T>;
export function mixins<T extends VueClass<Vue>[]>(...Ctors: T): MixedVueClass<T>;
export function mixins(...Ctors) {
  return Vue.extend({ mixins: Ctors })
}
